import React, { useState, useEffect, useCallback, useRef } from 'react';
import { orderAPI } from '../../services/api';
import socketService from '../../services/socket';
import Navbar from '../../components/Navbar';
import LiveTracking from '../../components/LiveTracking';
import useLocation from '../../hooks/useLocation';
import { useAuth } from '../../context/AuthContext';

function LocationUpdate() {
  const { user } = useAuth(); // Use AuthContext for authenticated user data
  const [activeOrders, setActiveOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(() => {
    // Load selected order from localStorage on mount
    return localStorage.getItem('deliverySelectedOrder') || null;
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [autoUpdate, setAutoUpdate] = useState(() => {
    // Load auto-update preference from localStorage on mount
    const saved = localStorage.getItem('deliveryAutoUpdate');
    return saved === 'true';
  });
  const { getAddressFromCurrentLocation, loading: locationLoading } = useLocation();
  const previousOrderRef = useRef(null);

  // Initialize socket connection and listeners
  useEffect(() => {
    // Connect to socket
    socketService.connect();

    // Listen for connection status
    const unsubConnection = socketService.on('connection', (connected) => {
      console.log('🔌 Socket connection status:', connected);
      setSocketConnected(connected);
    });

    // Listen for order updates (status changes)
    const unsubOrderUpdate = socketService.on('order_update', (data) => {
      console.log('📡 Order update received:', data);
      // Refresh orders when status changes
      fetchActiveOrders();
    });

    // Listen for order cancellations
    const unsubOrderCancelled = socketService.on('order_cancelled', (data) => {
      console.log('❌ Order cancelled:', data);
      // Remove cancelled order from active orders
      setActiveOrders(prev => prev.filter(o => o._id !== data.orderId));
      // Clear selection if cancelled order was selected
      if (selectedOrder === data.orderId) {
        setSelectedOrder(null);
        localStorage.removeItem('deliverySelectedOrder');
      }
    });

    // Join delivery room using authenticated user from AuthContext
    if (user?._id) {
      socketService.joinDeliveryRoom(user._id);
    }

    return () => {
      // Cleanup socket listeners
      unsubConnection();
      unsubOrderUpdate();
      unsubOrderCancelled();
    };
  }, [user]); // Add user as dependency to re-join room if user changes

  // Join order room when selected order changes
  useEffect(() => {
    if (previousOrderRef.current && previousOrderRef.current !== selectedOrder) {
      // Leave previous order room
      socketService.leaveOrderRoom(previousOrderRef.current);
    }

    if (selectedOrder) {
      // Join new order room
      socketService.joinOrderRoom(selectedOrder);
      previousOrderRef.current = selectedOrder;
    }
  }, [selectedOrder]);

  useEffect(() => {
    fetchActiveOrders();
    // Get initial location for map display
    fetchInitialLocation();
  }, []);

  const fetchInitialLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setLocationError(null);
        console.log('✅ Initial location fetched:', position.coords.latitude, position.coords.longitude);
      },
      (error) => {
        let errorMessage = 'Failed to get your location. ';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Please enable location permissions in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out.';
            break;
          default:
            errorMessage += 'An unknown error occurred.';
        }
        setLocationError(errorMessage);
        console.error('Location fetch error:', error);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Save auto-update preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('deliveryAutoUpdate', autoUpdate);
  }, [autoUpdate]);

  // Save selected order to localStorage when it changes
  useEffect(() => {
    if (selectedOrder) {
      localStorage.setItem('deliverySelectedOrder', selectedOrder);
    }
  }, [selectedOrder]);

  // Auto-update location every 30 seconds if enabled
  useEffect(() => {
    if (!autoUpdate || !selectedOrder) return;

    const interval = setInterval(() => {
      updateLocation();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoUpdate, selectedOrder]);

  const fetchActiveOrders = async () => {
    try {
      // Fetch delivery partner's active orders
      const response = await orderAPI.getDeliveryPartnerOrders();
      const orders = response.data.data.orders || [];

      // Debug logging for restaurant location
      console.log('📦 Raw API response:', response.data);
      orders.forEach((order, idx) => {
        console.log(`📍 Order ${idx} restaurant data:`, {
          orderId: order._id,
          orderNumber: order.orderNumber,
          status: order.status,
          restaurantId: order.restaurantId,
          restaurantLocation: order.restaurantId?.location,
          hasLat: order.restaurantId?.location?.lat,
          hasLng: order.restaurantId?.location?.lng
        });
      });

      // Filter only active delivery orders
      const active = orders.filter(order =>
        ['assigned_delivery', 'picked_up', 'on_the_way'].includes(order.status)
      );

      setActiveOrders(active);
      if (active.length > 0) {
        setSelectedOrder(active[0]._id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      setLoading(false);
    }
  };

  const updateLocation = async () => {
    if (!selectedOrder) {
      alert('Please select an order first');
      return;
    }

    setUpdating(true);
    try {
      // Get current location
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });

      const { latitude, longitude } = position.coords;

      // Update local state for map display
      setCurrentLocation({
        lat: latitude,
        lng: longitude
      });

      // Send location to backend via HTTP API (for persistence)
      await orderAPI.updateLocation(selectedOrder, {
        lat: latitude,
        lng: longitude
      });

      // Also emit via socket for real-time updates to customers tracking the order
      socketService.sendLocationUpdate(selectedOrder, latitude, longitude);
      console.log('📍 Location sent via socket:', { orderId: selectedOrder, lat: latitude, lng: longitude });

      setLastUpdate(new Date());
      setUpdating(false);
    } catch (error) {
      console.error('Location update error:', error);
      alert(error.message || 'Failed to update location. Please enable location permissions.');
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
        <Navbar />
        <div className="container">
          <div className="loading">🔄 Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            📍 Location Tracker
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Update your location for live tracking
          </p>
        </div>

        {/* Navigation Map */}
        {activeOrders.length > 0 && selectedOrder && (() => {
          const order = activeOrders.find(o => o._id === selectedOrder);
          if (!order) return null;

          // Determine destination based on order status
          // Before pickup (assigned_delivery): Show route to restaurant
          // After pickup (picked_up, on_the_way): Show route to delivery address
          let destination;
          if (order.status === 'assigned_delivery') {
            // Restaurant location - handle multiple possible structures
            const restaurant = order.restaurantId;
            let loc = null;

            // Try different location structures
            if (restaurant?.location?.lat && restaurant?.location?.lng) {
              loc = restaurant.location;
            } else if (restaurant?.address?.coordinates?.lat && restaurant?.address?.coordinates?.lng) {
              loc = restaurant.address.coordinates;
            } else if (restaurant?.address?.location?.lat && restaurant?.address?.location?.lng) {
              loc = restaurant.address.location;
            }

            console.log('🔍 Restaurant location extraction:', {
              restaurant,
              locationDirect: restaurant?.location,
              addressCoords: restaurant?.address?.coordinates,
              addressLocation: restaurant?.address?.location,
              extracted: loc
            });

            destination = loc?.lat && loc?.lng ? { lat: loc.lat, lng: loc.lng } : null;
          } else {
            // Delivery address coordinates
            destination = order.deliveryAddress?.coordinates;
          }

          const destinationLabel = order.status === 'assigned_delivery'
            ? '🏪 Restaurant Pickup'
            : '📍 Delivery Destination';

          // Debug logging
          console.log('🗺️ Map Debug:', {
            orderStatus: order.status,
            currentLocation,
            destination,
            restaurantData: order.restaurantId,
            deliveryAddress: order.deliveryAddress
          });

          return (
            <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)', padding: 0, overflow: 'hidden' }}>
              <div style={{
                padding: 'var(--space-md)',
                background: 'var(--gradient-primary)',
                color: 'white',
                fontWeight: '700',
                fontSize: 'var(--font-size-lg)',
                textAlign: 'center'
              }}>
                🧭 Navigation: {destinationLabel}
              </div>
              {destination ? (
                <LiveTracking
                  deliveryLocation={currentLocation}
                  destinationLocation={destination}
                  orderStatus={order.status}
                />
              ) : (
                <div style={{
                  padding: 'var(--space-2xl)',
                  textAlign: 'center',
                  background: 'var(--gray-100)',
                  color: 'var(--gray-600)'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: 'var(--space-md)' }}>📍</div>
                  <p>Destination location not available.</p>
                  <p style={{ fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-sm)' }}>
                    {order.status === 'assigned_delivery'
                      ? 'Restaurant location missing in database.'
                      : 'Delivery address coordinates missing.'}
                  </p>
                </div>
              )}
            </div>
          );
        })()}

        {activeOrders.length === 0 ? (
          <div className="card fade-in" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
            <div style={{ fontSize: '80px', marginBottom: 'var(--space-lg)' }}>📦</div>
            <h2 style={{ color: 'var(--gray-700)', marginBottom: 'var(--space-md)' }}>
              No Active Deliveries
            </h2>
            <p style={{ color: 'var(--gray-600)' }}>
              You don't have any active delivery orders at the moment.
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-xl)' }}>
            {/* Active Orders List */}
            <div className="card fade-in">
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-lg)',
                color: 'var(--gray-900)'
              }}>
                🚴 Active Deliveries
              </h2>

              {activeOrders.map((order) => (
                <div
                  key={order._id}
                  onClick={() => setSelectedOrder(order._id)}
                  style={{
                    padding: 'var(--space-md)',
                    marginBottom: 'var(--space-md)',
                    background: selectedOrder === order._id ? 'var(--gradient-primary)' : 'var(--gray-100)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    transition: 'all var(--transition-base)',
                    border: selectedOrder === order._id ? '2px solid var(--primary)' : '2px solid transparent'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h3 style={{
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: '700',
                        color: selectedOrder === order._id ? 'white' : 'var(--gray-900)',
                        marginBottom: 'var(--space-xs)'
                      }}>
                        Order #{order.orderNumber}
                      </h3>
                      <p style={{
                        margin: 0,
                        fontSize: 'var(--font-size-sm)',
                        color: selectedOrder === order._id ? 'rgba(255,255,255,0.9)' : 'var(--gray-600)'
                      }}>
                        {order.deliveryAddress.street}, {order.deliveryAddress.city}
                      </p>
                    </div>
                    <div style={{
                      padding: 'var(--space-xs) var(--space-sm)',
                      background: selectedOrder === order._id ? 'rgba(255,255,255,0.2)' : 'var(--success)',
                      color: selectedOrder === order._id ? 'white' : 'white',
                      borderRadius: 'var(--radius-full)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: '700',
                      textTransform: 'uppercase'
                    }}>
                      {order.status.replace(/_/g, ' ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Location Update Panel */}
            <div className="card fade-in">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-lg)' }}>
                <h2 style={{
                  fontSize: 'var(--font-size-2xl)',
                  fontWeight: '700',
                  color: 'var(--gray-900)',
                  margin: 0
                }}>
                  📡 Update Location
                </h2>
                {/* Socket connection indicator */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)',
                  padding: 'var(--space-xs) var(--space-sm)',
                  background: socketConnected ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  borderRadius: 'var(--radius-full)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: '600'
                }}>
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: socketConnected ? 'var(--success)' : '#EF4444',
                    animation: socketConnected ? 'pulse 2s infinite' : 'none'
                  }} />
                  <span style={{ color: socketConnected ? 'var(--success)' : '#EF4444' }}>
                    {socketConnected ? 'Live' : 'Offline'}
                  </span>
                </div>
              </div>

              {selectedOrder && (
                <>
                  {/* Auto-update toggle */}
                  <div style={{
                    padding: 'var(--space-md)',
                    background: 'var(--gray-100)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-lg)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <h4 style={{ margin: 0, marginBottom: 'var(--space-xs)', color: 'var(--gray-900)' }}>
                        🔄 Auto Update
                      </h4>
                      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                        Update location every 30 seconds
                      </p>
                    </div>
                    <label style={{ position: 'relative', display: 'inline-block', width: '60px', height: '34px' }}>
                      <input
                        type="checkbox"
                        checked={autoUpdate}
                        onChange={(e) => setAutoUpdate(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: 'absolute',
                        cursor: 'pointer',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: autoUpdate ? 'var(--success)' : 'var(--gray-400)',
                        transition: '0.4s',
                        borderRadius: '34px'
                      }}>
                        <span style={{
                          position: 'absolute',
                          content: '',
                          height: '26px',
                          width: '26px',
                          left: autoUpdate ? '30px' : '4px',
                          bottom: '4px',
                          background: 'white',
                          transition: '0.4s',
                          borderRadius: '50%'
                        }} />
                      </span>
                    </label>
                  </div>

                  {/* Location error warning */}
                  {locationError && (
                    <div style={{
                      padding: 'var(--space-md)',
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid #EF4444',
                      borderRadius: 'var(--radius-md)',
                      marginBottom: 'var(--space-lg)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--space-sm)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
                        <span style={{ fontSize: '20px' }}>⚠️</span>
                        <span style={{ fontWeight: '600', color: '#EF4444' }}>Location Access Required</span>
                      </div>
                      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)' }}>
                        {locationError}
                      </p>
                      <button
                        onClick={fetchInitialLocation}
                        className="btn btn-sm"
                        style={{
                          background: '#EF4444',
                          color: 'white',
                          border: 'none',
                          marginTop: 'var(--space-xs)'
                        }}
                      >
                        🔄 Retry Location Access
                      </button>
                    </div>
                  )}

                  {/* Current location status */}
                  {currentLocation && !locationError && (
                    <div style={{
                      padding: 'var(--space-md)',
                      background: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid var(--success)',
                      borderRadius: 'var(--radius-md)',
                      marginBottom: 'var(--space-lg)',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: 'var(--space-xs)' }}>✅</div>
                      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', fontWeight: '600', color: 'var(--success)' }}>
                        GPS Ready
                      </p>
                      <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--gray-600)', marginTop: 'var(--space-xs)' }}>
                        Lat: {currentLocation.lat.toFixed(6)}, Lng: {currentLocation.lng.toFixed(6)}
                      </p>
                    </div>
                  )}

                  {/* Manual update button */}
                  <button
                    onClick={updateLocation}
                    disabled={updating}
                    className="btn btn-primary btn-lg"
                    style={{
                      width: '100%',
                      marginBottom: 'var(--space-lg)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 'var(--space-sm)'
                    }}
                  >
                    <span style={{ fontSize: '24px' }}>{updating ? '🔄' : '📍'}</span>
                    <span>{updating ? 'Updating Location...' : 'Update Location Now'}</span>
                  </button>

                  {/* Last update info */}
                  {lastUpdate && (
                    <div style={{
                      padding: 'var(--space-md)',
                      background: 'rgba(81, 207, 102, 0.1)',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--success)',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '32px', marginBottom: 'var(--space-sm)' }}>✅</div>
                      <h4 style={{ margin: 0, marginBottom: 'var(--space-xs)', color: 'var(--success)' }}>
                        Location Updated
                      </h4>
                      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>
                        Last updated: {lastUpdate.toLocaleTimeString()}
                      </p>
                    </div>
                  )}

                  {/* Instructions */}
                  <div style={{
                    marginTop: 'var(--space-lg)',
                    padding: 'var(--space-md)',
                    background: 'var(--gray-100)',
                    borderRadius: 'var(--radius-md)'
                  }}>
                    <h4 style={{ margin: 0, marginBottom: 'var(--space-sm)', color: 'var(--gray-900)' }}>
                      💡 Tips
                    </h4>
                    <ul style={{ margin: 0, paddingLeft: 'var(--space-lg)', color: 'var(--gray-600)', fontSize: 'var(--font-size-sm)' }}>
                      <li>Enable auto-update for continuous tracking</li>
                      <li>Make sure location services are enabled</li>
                      <li>Keep the app open while delivering</li>
                      <li>Manual update works instantly</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LocationUpdate;
